import { toJpeg, toPng } from 'html-to-image';

import { fixCaptureDomIds, promoteSvgImageHrefs } from './fixCaptureDomIds';
import type { PreviewCaptureFormat, PreviewCaptureResult } from './previewCaptureRegistry';
import { embedPreviewFonts, inlinePublicStorageImages } from './previewCaptureResources';

/** 1×1 transparent GIF. A failed asset stays blank instead of rejecting the capture. */
const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function imageSource(target: EventTarget | null): string {
  if (target instanceof HTMLImageElement) {
    return target.currentSrc || target.src;
  }
  if (target instanceof SVGImageElement) {
    return target.href?.baseVal || target.getAttribute('href') || '';
  }
  return '';
}

/**
 * html-to-image rejects with the image's error event when an asset 404s or
 * cannot be fetched. Swap in a transparent pixel so the rest of the frame
 * still rasterizes.
 */
export function keepCaptureGoingAfterImageError(event: Event | string): void {
  if (typeof event === 'string') {
    return;
  }
  const target = event.currentTarget ?? event.target;
  if (target instanceof HTMLImageElement) {
    target.onerror = null;
    target.removeAttribute('srcset');
    target.src = TRANSPARENT_PIXEL;
    return;
  }
  if (target instanceof SVGImageElement) {
    target.onerror = null;
    target.setAttribute('href', TRANSPARENT_PIXEL);
  }
}

export function describeCaptureFailure(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof Event !== 'undefined' && error instanceof Event) {
    const src = imageSource(error.target).replace(/[?#].*$/, '');
    return src
      ? `Preview image failed to load: ${src}`
      : `Preview capture failed (${error.type} event).`;
  }
  return 'Preview capture failed.';
}

/** A same-origin SVG copied here becomes a bitmap at its intrinsic size and scales up soft. */
function isSvgImageSource(src: string): boolean {
  return /\.svg([?#]|$)/i.test(src);
}

/** Same-origin rasters can be copied without a second fetch. Cross-origin ones taint the canvas. */
function inlineDecodedImages(root: ParentNode): void {
  root.querySelectorAll('img').forEach((img) => {
    if (
      !img.complete ||
      img.naturalWidth === 0 ||
      img.src.startsWith('data:') ||
      isSvgImageSource(img.src)
    ) {
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      context.drawImage(img, 0, 0);
      img.src = canvas.toDataURL('image/png');
    } catch {
      // Leave the URL for html-to-image to fetch.
    }
  });
}

export async function rasterizePreviewElement(
  element: HTMLElement,
  options: { format: PreviewCaptureFormat; quality?: number; signal?: AbortSignal },
): Promise<PreviewCaptureResult> {
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  if (width <= 0 || height <= 0) {
    throw new Error('The preview frame has no visible size.');
  }

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await document.fonts.ready;
  }

  promoteSvgImageHrefs(element);
  fixCaptureDomIds(element);
  inlineDecodedImages(element);
  const { css: fontEmbedCSS, warnings: fontWarnings } = await embedPreviewFonts(
    element,
    options.signal,
  );
  const { restore: restoreImages, warnings: imageWarnings } = await inlinePublicStorageImages(
    element,
    options.signal,
  );
  const warnings = [...fontWarnings, ...imageWarnings];

  const rasterOptions = {
    // Supplying this (including an empty string) bypasses html-to-image's global CSS scan.
    fontEmbedCSS,
    // A timestamp query misses the URL the <img> already loaded, including signed
    // CDN urls and `?width=` resizes. Keep that query string as the cache key.
    includeQueryParams: true,
    imagePlaceholder: TRANSPARENT_PIXEL,
    onImageErrorHandler: keepCaptureGoingAfterImageError,
    // html-to-image defaults this to devicePixelRatio. width/height are CSS
    // pixels, so a 2x display would return a bitmap that does not match them.
    pixelRatio: 1,
    fetchRequestInit: options.signal ? { signal: options.signal } : undefined,
    // The capture host sits off-screen. Force the clone visible so inherited
    // visibility does not rasterize as an empty JPEG/PNG.
    style: { visibility: 'visible', opacity: '1' },
  };
  let dataUrl: string;
  try {
    dataUrl =
      options.format === 'png'
        ? await toPng(element, rasterOptions)
        : await toJpeg(element, { ...rasterOptions, quality: options.quality ?? 0.85 });
  } finally {
    // Keep later captures able to re-read an asset replaced at the same public URL.
    restoreImages();
  }

  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  if (!base64) {
    throw new Error('Preview capture returned an empty image.');
  }

  return {
    base64,
    mimeType: options.format === 'png' ? 'image/png' : 'image/jpeg',
    width,
    height,
    ...(warnings.length > 0 ? { warnings } : {}),
  };
}
